import { useState } from 'react';

import { Typography as Text } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { RiErrorWarningLine } from '@remixicon/react';

const FRIENDLY_MESSAGE = 'Something went wrong.';

// Best-effort friendly summary extraction. Handles the shapes we've seen:
// - gRPC:          "rpc error: ... details = \"<friendly>\" ..."
// - Anthropic API: "API Error: 400 {\"type\":\"error\",\"error\":{\"message\":\"<friendly>\",...}}"
// Falls back to the generic friendly message when nothing clean can be pulled.
function extractFriendlySummary(message: string): string {
  const grpc = message.match(/details\s*=\s*"([^"]+)"/);
  if (grpc) return grpc[1];

  const jsonStart = message.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const parsed: unknown = JSON.parse(message.slice(jsonStart));
      if (
        parsed &&
        typeof parsed === 'object' &&
        'error' in parsed &&
        parsed.error &&
        typeof parsed.error === 'object' &&
        'message' in parsed.error &&
        typeof (parsed.error as { message: unknown }).message === 'string'
      ) {
        return (parsed.error as { message: string }).message;
      }
    } catch {
      // Fall through to the generic friendly message.
    }
  }

  return FRIENDLY_MESSAGE;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--encore-spacing-tighter-2)',
    padding: 'var(--encore-spacing-tighter-2) var(--encore-spacing-tighter)',
    backgroundColor: 'var(--background-negative-subdued-base)',
    borderLeft: '3px solid var(--essential-negative)',
    borderRadius: 'var(--encore-corner-radius-base)',
  },
  icon: {
    marginTop: 2,
    flexShrink: 0,
    color: 'var(--essential-negative)',
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  toggle: {
    background: 'none',
    border: 'none',
    color: 'var(--text-subdued)',
    cursor: 'pointer',
    display: 'block',
    fontSize: 'var(--encore-text-size-smaller-3)',
    marginTop: 'var(--encore-spacing-tighter-4)',
    padding: 0,
  },
  details: {
    display: 'block',
    marginTop: 'var(--encore-spacing-tighter-2)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
});

export const ChatErrorMessage = ({
  message,
  code,
}: {
  message: string;
  code?: string | null;
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const summary = extractFriendlySummary(message);
  const hasDetails = message.length > 0 && summary !== message;

  return (
    <div className={classes.root}>
      <RiErrorWarningLine size={16} className={classes.icon} />
      <div className={classes.content}>
        <Text variant="body2">
          {summary}
          {code ? (
            <Text component="span" variant="body2" color="textSecondary">
              {` · ${code}`}
            </Text>
          ) : null}
        </Text>
        {hasDetails && (
          <>
            {expanded && (
              <div className={classes.details}>
                <Text variant="caption" color="textSecondary">
                  {message}
                </Text>
              </div>
            )}
            <button
              type="button"
              className={classes.toggle}
              onClick={() => setExpanded(v => !v)}
            >
              {expanded ? 'Hide details' : 'Show details'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
